import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import QRCode from "qrcode";
import { ApiError, api, apiErrorCode, formatBRL } from "../lib/api.js";
import { isPixUnavailableError } from "../lib/apiErrors.js";
import { formatBillingDate } from "../lib/formatBillingDate.js";
import {
  PIX_CHARGE_TTL_MS,
  copyPixEmvToClipboard,
  ensureActivePixOrder,
  isPixOrderActive,
  loadStoredPixEmv,
  pixCodeState,
  pixExpiresInMs,
  regeneratePixOrder,
  resolvePixEmv,
  type PixOrderSnapshot,
} from "../lib/pixCheckout.js";
import type { BillingInterval, Plan } from "../../../shared/types.js";

async function renderQr(emv: string): Promise<string> {
  return QRCode.toDataURL(emv, { width: 320, margin: 1, errorCorrectionLevel: "M" });
}

export type IntegratedPixPanelProps = {
  orderId: string;
  initialEmv?: string | null;
  plan: Plan;
  interval: BillingInterval;
  onBack: () => void;
  /** Called after payment/authorization is confirmed. */
  onPaid: (order: PixOrderSnapshot) => void;
  /** A line above the code, for flows where approving it is not a payment ("Manter assinatura"). */
  notice?: string;
  /** i18n key of the title (takes `minutes`); defaults to the payment one. */
  titleKey?: string;
  /** Replaces the "payment approved" screen's title and line. */
  paidTitle?: string;
  paidMessage?: string;
  /**
   * The code only authorizes Pix Automático, nothing is charged today (a plan change): known from the
   * /change answer, so the title is right before the order loads.
   */
  authorizeOnly?: boolean;
  /** Woovi could not make a new code: the same change by card (absent on "Manter assinatura", PIX only). */
  onSwitchToCard?: () => void;
};

/**
 * Inline PIX QR for /integrated — keeps the customer in the billing sheet
 * instead of opening /pix/pending in a popup or top-level navigation.
 */
export function IntegratedPixPanel({
  orderId,
  initialEmv,
  plan,
  interval,
  onBack,
  onPaid,
  notice,
  titleKey,
  paidTitle,
  paidMessage,
  authorizeOnly,
  onSwitchToCard,
}: IntegratedPixPanelProps) {
  const { t, i18n } = useTranslation();
  const [order, setOrder] = useState<PixOrderSnapshot | null>(null);
  const [emv, setEmv] = useState(() => initialEmv ?? loadStoredPixEmv(orderId));
  const [qrSrc, setQrSrc] = useState("");
  const [copied, setCopied] = useState(false);
  const emvTextareaRef = useRef<HTMLTextAreaElement>(null);
  // The deadline, not a countdown: a phone pauses timers while the customer is in the bank app, so
  // the time left is read off the clock and the server's pix_expires_at (PIT-546 b).
  const [expiresAtMs, setExpiresAtMs] = useState(() => Date.now() + PIX_CHARGE_TTL_MS);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [recheckingNow, setRecheckingNow] = useState(false);
  const [recheckHint, setRecheckHint] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preparingPix, setPreparingPix] = useState(true);
  const [paid, setPaid] = useState(false);
  const paymentHandledRef = useRef(false);
  const regeneratingRef = useRef(false);
  // Bumped when a new code is asked for: an order read that started before it carries the old code's
  // deadline, and applying it would expire the new code at once and ask for yet another.
  const regenGenRef = useRef(0);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  const applyEmv = useCallback(async (nextEmv: string) => {
    if (!nextEmv) return;
    setEmv(nextEmv);
    try {
      setQrSrc(await renderQr(nextEmv));
      setLoadError(null);
    } catch {
      setLoadError("qr_render_failed");
    }
  }, []);

  const onPaymentIdentified = useCallback((r: PixOrderSnapshot) => {
    if (paymentHandledRef.current) return;
    paymentHandledRef.current = true;
    setPaid(true);
    setOrder(r);
    onPaidRef.current(r);
  }, []);

  /** The order as the server has it now: its status, and the deadline of the code it holds. */
  const applyOrder = useCallback((r: PixOrderSnapshot) => {
    setOrder(r);
    const left = pixExpiresInMs(r);
    if (left != null) setExpiresAtMs(Date.now() + left);
    setNowMs(Date.now());
  }, []);

  const displayEmv = resolvePixEmv(orderId, emv, order);

  useEffect(() => {
    let cancelled = false;
    const cached = loadStoredPixEmv(orderId);
    if (cached) void applyEmv(cached);
    void (async () => {
      setPreparingPix(true);
      try {
        if (initialEmv) await applyEmv(initialEmv);
        let data = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
        if (cancelled) return;
        if (data.status === "pending" && data.payment_method === "openpix" && !isPixOrderActive(data)) {
          // A new code on the way: reads answering meanwhile carry the expired one, and the clock
          // must not ask for another at the same time.
          regeneratingRef.current = true;
          regenGenRef.current += 1;
          try {
            data = await ensureActivePixOrder(orderId);
          } finally {
            regeneratingRef.current = false;
          }
        }
        if (cancelled) return;
        applyOrder(data);
        if (data.pix_emv) {
          sessionStorage.setItem(`pix_emv_${orderId}`, data.pix_emv);
          await applyEmv(data.pix_emv);
        } else if (!initialEmv && !cached) {
          setLoadError("missing_emv");
        }
        if (data.status === "paid") onPaymentIdentified(data);
      } catch (err) {
        if (cancelled) return;
        if (isPixUnavailableError(err)) setLoadError("pix_unavailable");
        else if (!initialEmv && !cached) setLoadError("fetch_failed");
      } finally {
        if (!cancelled) setPreparingPix(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per orderId / initialEmv
  }, [orderId, initialEmv]);

  const pollOrderStatus = useCallback(async () => {
    if (paymentHandledRef.current) return;
    const gen = regenGenRef.current;
    try {
      const r = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
      if (r.status === "paid") {
        onPaymentIdentified(r);
        return;
      }
      if (gen === regenGenRef.current && !regeneratingRef.current) applyOrder(r);
    } catch {
      /* keep polling */
    }
  }, [orderId, onPaymentIdentified, applyOrder]);

  const recheckWithProvider = useCallback(async (): Promise<boolean> => {
    if (paymentHandledRef.current) return false;
    const gen = regenGenRef.current;
    try {
      await api(`/api/public/orders/${orderId}/recheck`, { method: "POST" });
      const r = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
      if (r.status === "paid") {
        onPaymentIdentified(r);
        return true;
      }
      if (gen === regenGenRef.current && !regeneratingRef.current) applyOrder(r);
      return false;
    } catch {
      return false;
    }
  }, [orderId, onPaymentIdentified, applyOrder]);

  useEffect(() => {
    if (paymentHandledRef.current) return;
    const handle = setInterval(() => void pollOrderStatus(), 10000);
    return () => clearInterval(handle);
  }, [pollOrderStatus]);

  useEffect(() => {
    if (paymentHandledRef.current) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // The screen is right at once, before the provider answers.
      setNowMs(Date.now());
      void recheckWithProvider();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [recheckWithProvider]);

  useEffect(() => {
    const h = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(h);
  }, []);

  const timeLeftMs = Math.max(0, expiresAtMs - nowMs);

  const applyRegenerated = useCallback(
    async (r: { pix_emv: string | null; pix_expires_at: string | null }) => {
      if (r.pix_emv) {
        sessionStorage.setItem(`pix_emv_${orderId}`, r.pix_emv);
        await applyEmv(r.pix_emv);
      } else {
        setLoadError("missing_emv");
      }
      if (r.pix_expires_at) setExpiresAtMs(new Date(r.pix_expires_at).getTime());
      setNowMs(Date.now());
      // The server puts a regenerated order back to pending.
      setOrder((prev) =>
        prev ? { ...prev, status: "pending", pix_emv: r.pix_emv, pix_expires_at: r.pix_expires_at } : prev,
      );
    },
    [orderId, applyEmv],
  );

  /** Why a new code could not be made, as the panel shows it. */
  const regenerateFailed = useCallback(
    (err: unknown) => {
      if (isPixUnavailableError(err)) {
        setLoadError("pix_unavailable");
      } else if (err instanceof ApiError && apiErrorCode(err) === "already_paid") {
        void pollOrderStatus();
      } else if (err instanceof ApiError && apiErrorCode(err) === "order_canceled") {
        setOrder((prev) => (prev ? { ...prev, status: "canceled" } : prev));
      } else {
        setLoadError("missing_emv");
      }
    },
    [pollOrderStatus],
  );

  useEffect(() => {
    if (paid || order?.status !== "pending" || timeLeftMs > 0) return;
    // After a failed attempt the button asks again, not the clock.
    if (regeneratingRef.current || loadError != null) return;
    regeneratingRef.current = true;
    regenGenRef.current += 1;
    let cancelled = false;
    void (async () => {
      try {
        const r = await regeneratePixOrder(orderId);
        if (cancelled) return;
        await applyRegenerated(r);
      } catch (err) {
        if (!cancelled) regenerateFailed(err);
      } finally {
        regeneratingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, order?.status, timeLeftMs, paid, loadError, applyRegenerated, regenerateFailed]);

  const minutes = Math.floor(timeLeftMs / 60000);
  const seconds = Math.floor((timeLeftMs % 60000) / 1000);
  const codeState = pixCodeState(order, nowMs);
  const closed = codeState === "closed";
  const expired = !closed && (timeLeftMs <= 0 || codeState === "expired");
  const timeStr = useMemo(
    () => `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
    [minutes, seconds],
  );

  const isMonthly = (order?.interval ?? interval) !== "annual";
  const isDeferredAuth = order != null && order.final_cents === 0 && !!order.first_charge_at;
  const authorizing = order ? isDeferredAuth : !!authorizeOnly;

  async function recheck() {
    setRecheckingNow(true);
    setRecheckHint(false);
    try {
      const paidNow = await recheckWithProvider();
      if (!paidNow && !paymentHandledRef.current) {
        setRecheckHint(true);
      }
    } finally {
      setRecheckingNow(false);
    }
  }

  async function regenerate() {
    if (regeneratingRef.current) return;
    regeneratingRef.current = true;
    regenGenRef.current += 1;
    setPreparingPix(true);
    setLoadError(null);
    try {
      await applyRegenerated(await regeneratePixOrder(orderId));
    } catch (err) {
      regenerateFailed(err);
    } finally {
      regeneratingRef.current = false;
      setPreparingPix(false);
    }
  }

  if (paid) {
    return (
      <div className="card-glass p-6 lg:p-8 text-center text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{paidTitle ?? t("paymentIdentified.title")}</div>
        <p className="text-sm text-ink-2 mt-2">{paidMessage ?? t("pix.paid")}</p>
      </div>
    );
  }

  const pixUnavailable = loadError === "pix_unavailable";
  // A way out whenever the code on screen cannot be used: never a "loading" with nothing to press.
  const needsNewCode = !closed && (expired || loadError === "missing_emv" || loadError === "fetch_failed");

  const unavailableBox = (
    <div className="mt-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-sm" role="status">
      <div className="font-medium">
        {onSwitchToCard ? t("checkout.pixUnavailable") : t("integrated.pixUnavailableRetry")}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {onSwitchToCard && (
          <button type="button" className="btn-primary" onClick={onSwitchToCard}>
            {t("checkout.pixUnavailableCta")}
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={() => void regenerate()} disabled={preparingPix}>
          {t("pix.regenerateCode")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)]">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-[color:var(--text-primary)] mb-4"
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4" aria-hidden />
        {t("integrated.manageTitle")}
      </button>

      <h1 className="text-xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
        {titleKey
          ? t(titleKey, { minutes: Math.max(1, minutes) })
          : authorizing
            ? t("pix.authorizeTitle")
            : t("pix.title", { minutes: Math.max(1, minutes) })}
      </h1>
      <p className="text-sm text-ink-2 mt-1">
        {t(`plans.${plan}`)} · {t(`plans.${interval}`)}
        {order?.final_cents != null && order.final_cents > 0
          ? ` · ${formatBRL(order.final_cents)}`
          : ""}
      </p>
      {!closed && (
        <p className="text-sm mt-2">
          <span className={`tabular-nums font-medium ${expired ? "text-danger" : "text-purple-deep"}`}>
            {timeStr}
          </span>
        </p>
      )}
      {notice && <p className="text-sm text-ink-2 mt-3 leading-relaxed">{notice}</p>}

      {isDeferredAuth && order?.first_charge_at && (
        <section className="mt-4 rounded-lg border border-purple-deep/25 bg-purple-deep/5 p-4 text-sm">
          <p className="text-ink-2">
            {isMonthly
              ? t("pix.billingSchedule.authorizeOnlyMonthly")
              : t("pix.billingSchedule.authorizeOnlyAnnual")}
          </p>
          <dl className="mt-3 space-y-2">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-2">{t("pix.billingSchedule.firstCharge")}</dt>
              <dd className="font-medium text-right">
                {formatBillingDate(order.first_charge_at, i18n.language)}
              </dd>
            </div>
            {order.recurring_cents != null && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-2">
                  {isMonthly
                    ? t("pix.billingSchedule.recurringMonthly")
                    : t("pix.billingSchedule.recurringAnnual")}
                </dt>
                <dd className="font-medium">{formatBRL(order.recurring_cents)}</dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {authorizing && !closed && (
        <section className="pix-howto mt-4" aria-label={t("pix.howTo.authorizeTitle")}>
          <h2 className="pix-howto__title">{t("pix.howTo.authorizeTitle")}</h2>
          <ol className="pix-howto__steps">
            {[
              t("pix.howTo.step1"),
              t("pix.howTo.step2"),
              t("pix.howTo.step3"),
              t("pix.howTo.step4AuthorizeOnly"),
            ].map((step, i) => (
              <li key={i} className="pix-howto__step">
                <span className="pix-howto__num" aria-hidden="true">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <p className="pix-howto__note">{t("pix.howTo.noteDeferred")}</p>
        </section>
      )}

      {closed ? (
        <div className="mt-4 p-3 rounded-md bg-black/5 text-sm" role="status">
          <div className="font-medium">{t("pix.codeClosed")}</div>
          <button type="button" className="btn-ghost mt-2" onClick={onBack}>
            {t("integrated.manageTitle")}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-center justify-center">
            {qrSrc ? (
              <img
                src={qrSrc}
                alt="PIX QR"
                className={`rounded-md w-64 h-64 bg-white p-2 ${expired ? "opacity-30" : ""}`}
              />
            ) : (
              <div className="w-64 h-64 bg-black/5 rounded-md flex flex-col items-center justify-center text-ink-2 text-sm text-center px-4 gap-2">
                {pixUnavailable ? (
                  <span>{t("pix.missingEmv")}</span>
                ) : needsNewCode && !preparingPix ? (
                  <>
                    <span>{expired ? t("pix.codeExpired") : t("pix.missingEmv")}</span>
                    <button type="button" className="btn-ghost text-xs" onClick={() => void regenerate()}>
                      {t("pix.regenerateCode")}
                    </button>
                  </>
                ) : preparingPix ? (
                  <>
                    <span className="font-medium">{t("integrated.openingPaymentHint")}</span>
                    <span className="text-xs">{t("common.loading")}</span>
                  </>
                ) : (
                  <span>{t("common.loading")}</span>
                )}
              </div>
            )}
          </div>

          {pixUnavailable && unavailableBox}

          {!pixUnavailable && needsNewCode && qrSrc && (
            <div className="mt-4 p-3 rounded-md bg-danger/10 border border-danger/30 text-sm">
              <div className="font-medium text-danger">{expired ? t("pix.codeExpired") : t("pix.missingEmv")}</div>
              <button
                type="button"
                className="btn-primary mt-2"
                onClick={() => void regenerate()}
                disabled={preparingPix}
              >
                {t("pix.regenerateCode")}
              </button>
            </div>
          )}

          <div className="mt-4">
            <div className="label mb-1">{t("pix.copyCode")}</div>
            <textarea
              ref={emvTextareaRef}
              readOnly
              className="input font-mono text-xs"
              rows={3}
              value={displayEmv}
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="btn-ghost flex-1"
                onClick={async () => {
                  const code = resolvePixEmv(orderId, emv, order);
                  const ok = await copyPixEmvToClipboard(code, emvTextareaRef.current);
                  if (!ok) return;
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? t("pix.copied") : t("pix.copy")}
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => void recheck()}
                disabled={recheckingNow}
              >
                {recheckingNow ? t("pix.checking") : authorizing ? t("pix.alreadyAuthorized") : t("pix.alreadyPaid")}
              </button>
            </div>
            {recheckHint && (
              <div
                className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-[color:var(--text-primary)]"
                role="status"
              >
                {t("pix.notIdentifiedYet")}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
